import { Tooltip } from 'components/ui/tooltip';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';
import { getTextClampedToLinesProps } from 'utils/text';

export type LineClampProps = {
    maxLineCount?: number;
    maxTooltipLineCount?: number;
    className?: string;
    keepIndentation?: boolean;
} & PropsWithChildren;

export default function LineClamp({
    maxLineCount = 3,
    maxTooltipLineCount = 15,
    children,
    className,
    keepIndentation = false,
}: LineClampProps) {
    const whiteSpaceClass = keepIndentation ? 'whitespace-pre-wrap' : 'whitespace-normal';
    const [open, setOpen] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    // A clamp counts LINE BOXES, so it constrains nothing when the text it was given renders inside
    // a flex or grid child — a `PartyName`, a link that lays itself out. Nothing about that fails
    // loudly: the content simply keeps wrapping, which is only noticed when a table row turns out
    // taller than its neighbours. So say it, in development, by checking the outcome rather than
    // guessing at the cause — the rendered height against the height the clamp promised. Decorative
    // flex children (a chip beside clamped text) are correctly silent, because the clamp still works.
    useEffect(() => {
        if (!import.meta.env.DEV) {
            return;
        }
        const element = textRef.current;
        if (!element) {
            return;
        }
        const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight);
        if (!Number.isFinite(lineHeight) || element.clientHeight <= lineHeight * (maxLineCount + 0.5)) {
            return;
        }
        console.warn(
            `LineClamp is not clamping: ${element.clientHeight}px of content where ${maxLineCount} ` +
                `line(s) is ${Math.round(lineHeight * maxLineCount)}px. A clamp counts line boxes, and ` +
                `this content has none of its own to count — it is probably a component that renders ` +
                `flex, rather than text. For a single line, EllipsisTooltip does the same job without ` +
                `needing them.`,
            element
        );
    }, [maxLineCount, children]);

    // The full text is offered only when the clamp has actually cut something off.
    const handleOpenChange = (nextOpen: boolean) => {
        const element = textRef.current;
        if (nextOpen && element && element.scrollHeight <= element.clientHeight) {
            return;
        }
        setOpen(nextOpen);
    };

    return (
        <span className={className}>
            <Tooltip
                delay={500}
                trackCursorAxis="both"
                disableHoverablePopup
                open={open}
                onOpenChange={handleOpenChange}
                content={
                    <span style={getTextClampedToLinesProps(maxTooltipLineCount)} className={whiteSpaceClass}>
                        {children}
                    </span>
                }
            >
                <span>
                    <span
                        ref={textRef}
                        style={getTextClampedToLinesProps(maxLineCount)}
                        className={cn(keepIndentation && whiteSpaceClass)}
                    >
                        {children}
                    </span>
                </span>
            </Tooltip>
        </span>
    );
}
